/**
 * Relative time for audit entries — "12 min ago".
 *
 * The mock stored strings like `'12 min ago'` literally, which is fine for a
 * still frame and wrong the moment the data is real: those strings were minted
 * once and would age into lies. The API sends ISO-8601 timestamps, so the
 * phrasing is computed at render.
 *
 * Uses `Intl.RelativeTimeFormat` rather than hand-written thresholds so the
 * pluralisation is the platform's problem, not ours.
 */
const RELATIVE = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

const DIVISIONS: readonly { amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
    { amount: 60, unit: 'second' },
    { amount: 60, unit: 'minute' },
    { amount: 24, unit: 'hour' },
    { amount: 7, unit: 'day' },
    { amount: 4.34524, unit: 'week' },
    { amount: 12, unit: 'month' },
    { amount: Number.POSITIVE_INFINITY, unit: 'year' },
];

export function relativeTime(iso: string): string {
    const timestamp = Date.parse(iso);
    // A malformed timestamp should show the raw value rather than "Invalid Date"
    // — in an audit log the unparseable string is itself the useful evidence.
    if (Number.isNaN(timestamp)) return iso;

    let duration = (timestamp - Date.now()) / 1000;

    for (const division of DIVISIONS) {
        if (Math.abs(duration) < division.amount) {
            return RELATIVE.format(Math.round(duration), division.unit);
        }
        duration /= division.amount;
    }

    return iso;
}

/** Absolute rendering, for the `title` tooltip behind a relative label. */
export function absoluteTime(iso: string): string {
    const timestamp = Date.parse(iso);
    if (Number.isNaN(timestamp)) return iso;
    return new Date(timestamp).toLocaleString();
}

/**
 * A calendar date, for a `createdAt` that is being read rather than watched.
 *
 * Distinct from {@link relativeTime}, which answers "how long ago" and is right
 * for an audit feed. A client's creation date is a fact you look up, and "8
 * months ago" is the wrong shape for that.
 *
 * `en-GB` is pinned rather than left to the browser. Three screens showed this
 * and two of them formatted it differently, side by side on the same page,
 * because one copy passed `undefined` for the locale. A shared function whose
 * output depends on who is looking is not shared.
 *
 * An unparseable value becomes an em dash. Upstream types `createdAt` as a
 * string, so a malformed one arrives as data, not as a crash — and printing
 * `Invalid Date` in a metadata rail reads as a bug in this app rather than as a
 * value that is not there.
 */
export function shortDate(iso: string): string {
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime())
        ? '—'
        : parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
